import { useTranslation } from 'react-i18next';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import type { InventoryMetrics } from '../../../api/queries/analytics.queries';

const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00C49F', '#FFBB28', '#FF8042', '#0088FE'];
const STATUS_COLORS = {
  normal: '#10B981',
  low: '#F59E0B',
  out_of_stock: '#EF4444',
  overstock: '#F97316',
};

export function StockStatusChart({ data }: { data?: InventoryMetrics['stockLevels'] }) {
  const { t } = useTranslation();

  if (!data || data.length === 0) {
    return <EmptyChart message={t('analytics.noDataAvailable')} />;
  }

  const statusCounts = data.reduce((acc, item) => {
    acc[item.status] = (acc[item.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const chartData = Object.entries(statusCounts).map(([status, count]) => ({
    name: t(`inventory.${status}`),
    value: count,
    status,
  }));

  return (
    <ResponsiveContainer width="100%" height={250}>
      <PieChart>
        <Pie
          data={chartData}
          cx="50%"
          cy="50%"
          innerRadius={50}
          outerRadius={80}
          fill="#8884d8"
          paddingAngle={2}
          dataKey="value"
          label={({ name, value }) => `${name}: ${value}`}
        >
          {chartData.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={STATUS_COLORS[entry.status as keyof typeof STATUS_COLORS] || COLORS[index % COLORS.length]}
            />
          ))}
        </Pie>
        <Tooltip />
      </PieChart>
    </ResponsiveContainer>
  );
}

export function IngredientUsageChart({ data }: { data?: InventoryMetrics['usageByIngredient'] }) {
  const { t } = useTranslation();

  if (!data || data.length === 0) {
    return <EmptyChart message={t('analytics.noDataAvailable')} />;
  }

  const chartData = data.slice(0, 10).map((item) => ({
    name: item.ingredientName.length > 15
      ? item.ingredientName.substring(0, 15) + '...'
      : item.ingredientName,
    fullName: item.ingredientName,
    cost: item.cost,
    used: item.used,
  }));

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={chartData} layout="vertical">
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis type="number" fontSize={12} />
        <YAxis dataKey="name" type="category" fontSize={11} width={100} />
        <Tooltip
          formatter={(value, name) => [
            name === 'cost' ? `฿${Number(value).toLocaleString()}` : value,
            name === 'cost' ? t('analytics.cost') : t('analytics.used'),
          ]}
          labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName || ''}
        />
        <Legend />
        <Bar dataKey="cost" fill="#8884d8" name={t('analytics.cost')} radius={[0, 4, 4, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

export function StockLevelsChart({ data }: { data?: InventoryMetrics['stockLevels'] }) {
  const { t } = useTranslation();

  if (!data || data.length === 0) {
    return <EmptyChart message={t('analytics.noDataAvailable')} />;
  }

  const chartData = data.slice(0, 10).map((item) => ({
    name: item.ingredientName.length > 12
      ? item.ingredientName.substring(0, 12) + '...'
      : item.ingredientName,
    fullName: item.ingredientName,
    current: item.currentStock,
    reorder: item.reorderLevel,
    max: item.maxLevel,
  }));

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={chartData}>
        <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
        <XAxis dataKey="name" fontSize={10} angle={-45} textAnchor="end" height={60} />
        <YAxis fontSize={12} />
        <Tooltip
          labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName || ''}
        />
        <Legend />
        <Bar dataKey="current" fill="#82ca9d" name={t('analytics.currentStock')} />
        <Bar dataKey="reorder" fill="#ffc658" name={t('analytics.reorderLevel')} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function EmptyChart({ message }: { message: string }) {
  return (
    <div className="h-[250px] flex items-center justify-center bg-secondary/20 rounded">
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  );
}
